import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { 
  ArrowLeft, 
  Plus, 
  Search,
  Edit,
  Trash2,
  Eye,
  EyeOff,
  Apple,
  Beef,
  Wheat,
  Droplets,
  Leaf,
  Milk,
  X,
  Filter
} from "lucide-react";
import { cn } from "../../lib/utils";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Predefined categories with colors
const CATEGORIES = [
  { id: "proteinas", label: "Proteínas", color: "text-red-400", bg: "bg-red-500/10", icon: Beef },
  { id: "carbohidratos", label: "Carbohidratos", color: "text-yellow-400", bg: "bg-yellow-500/10", icon: Wheat },
  { id: "grasas_saludables", label: "Grasas saludables", color: "text-blue-400", bg: "bg-blue-500/10", icon: Droplets },
  { id: "verduras", label: "Verduras", color: "text-green-400", bg: "bg-green-500/10", icon: Leaf },
  { id: "frutas", label: "Frutas", color: "text-orange-400", bg: "bg-orange-500/10", icon: Apple },
  { id: "lacteos", label: "Lácteos", color: "text-cyan-400", bg: "bg-cyan-500/10", icon: Milk },
  { id: "legumbres", label: "Legumbres", color: "text-amber-400", bg: "bg-amber-500/10", icon: Wheat },
  { id: "suplementos", label: "Suplementos", color: "text-purple-400", bg: "bg-purple-500/10", icon: Droplets },
  { id: "otros", label: "Otros", color: "text-gray-400", bg: "bg-gray-500/10", icon: Apple },
];

const UNITS = [
  { id: "g", label: "Gramos (g)" },
  { id: "ml", label: "Mililitros (ml)" },
  { id: "uds", label: "Unidades (uds)" },
];

// Empty food template
const EMPTY_FOOD = {
  name: "",
  category: "proteinas",
  unit: "g",
  default_amount: 100,
  calories_per_100: 0,
  protein_per_100: 0,
  carbs_per_100: 0,
  fat_per_100: 0,
  fiber_per_100: 0,
  notes: "",
  active: true
};

// Food Form Component
const FoodForm = ({ food, onSave, onCancel, saving }) => {
  const [formData, setFormData] = useState(food || EMPTY_FOOD);

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const getCategoryInfo = (catId) => CATEGORIES.find(c => c.id === catId) || CATEGORIES[0];

  return (
    <div className="space-y-6 max-h-[70vh] overflow-y-auto pr-2">
      {/* Nombre */}
      <div>
        <label className="text-[#888] text-xs block mb-1">Nombre del alimento *</label>
        <Input
          value={formData.name}
          onChange={(e) => updateField("name", e.target.value)}
          placeholder="Ej: Pechuga de pollo"
          className="bg-[#1A1A1A] border-[#2A2A2A] text-white"
          data-testid="food-name-input"
        />
      </div>

      {/* Categoría y Unidad */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="text-[#888] text-xs block mb-1">Categoría</label>
          <Select value={formData.category} onValueChange={(v) => updateField("category", v)}>
            <SelectTrigger className="bg-[#1A1A1A] border-[#2A2A2A] text-white" data-testid="food-category-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#1A1A1A] border-[#2A2A2A]">
              {CATEGORIES.map(cat => (
                <SelectItem key={cat.id} value={cat.id} className="text-white hover:bg-[#2A2A2A]">
                  <span className={cat.color}>{cat.label}</span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-[#888] text-xs block mb-1">Unidad</label>
          <Select value={formData.unit} onValueChange={(v) => updateField("unit", v)}>
            <SelectTrigger className="bg-[#1A1A1A] border-[#2A2A2A] text-white" data-testid="food-unit-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#1A1A1A] border-[#2A2A2A]">
              {UNITS.map(unit => (
                <SelectItem key={unit.id} value={unit.id} className="text-white hover:bg-[#2A2A2A]">
                  {unit.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Cantidad por defecto */}
      <div>
        <label className="text-[#888] text-xs block mb-1">Cantidad por defecto</label>
        <div className="flex items-center gap-2">
          <Input
            type="number"
            value={formData.default_amount}
            onChange={(e) => updateField("default_amount", parseInt(e.target.value) || 0)}
            className="bg-[#1A1A1A] border-[#2A2A2A] text-white w-32"
            data-testid="food-default-amount-input"
          />
          <span className="text-[#666] text-sm">{formData.unit}</span>
        </div>
      </div>

      {/* Macros por 100g/100ml/unidad */}
      <div>
        <label className="text-[#888] text-xs block mb-2">
          Macros por {formData.unit === "uds" ? "unidad" : `100${formData.unit}`}
        </label>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
          <div>
            <label className="text-[#666] text-[10px] block mb-1">Calorías</label>
            <Input
              type="number"
              value={formData.calories_per_100}
              onChange={(e) => updateField("calories_per_100", parseFloat(e.target.value) || 0)}
              className="bg-[#1A1A1A] border-[#2A2A2A] text-[#D4AF37] text-sm"
              data-testid="food-calories-input"
            />
          </div>
          <div>
            <label className="text-[#666] text-[10px] block mb-1">Proteína (g)</label>
            <Input
              type="number"
              step="0.1"
              value={formData.protein_per_100}
              onChange={(e) => updateField("protein_per_100", parseFloat(e.target.value) || 0)}
              className="bg-[#1A1A1A] border-[#2A2A2A] text-red-400 text-sm"
              data-testid="food-protein-input"
            />
          </div>
          <div>
            <label className="text-[#666] text-[10px] block mb-1">Carbos (g)</label>
            <Input
              type="number"
              step="0.1"
              value={formData.carbs_per_100}
              onChange={(e) => updateField("carbs_per_100", parseFloat(e.target.value) || 0)}
              className="bg-[#1A1A1A] border-[#2A2A2A] text-yellow-400 text-sm"
              data-testid="food-carbs-input"
            />
          </div>
          <div>
            <label className="text-[#666] text-[10px] block mb-1">Grasas (g)</label>
            <Input
              type="number"
              step="0.1"
              value={formData.fat_per_100}
              onChange={(e) => updateField("fat_per_100", parseFloat(e.target.value) || 0)}
              className="bg-[#1A1A1A] border-[#2A2A2A] text-blue-400 text-sm"
              data-testid="food-fat-input"
            />
          </div>
          <div>
            <label className="text-[#666] text-[10px] block mb-1">Fibra (g)</label>
            <Input
              type="number"
              step="0.1"
              value={formData.fiber_per_100}
              onChange={(e) => updateField("fiber_per_100", parseFloat(e.target.value) || 0)}
              className="bg-[#1A1A1A] border-[#2A2A2A] text-green-400 text-sm"
              data-testid="food-fiber-input"
            />
          </div>
        </div>
      </div>

      {/* Notas */}
      <div>
        <label className="text-[#888] text-xs block mb-1">Notas</label>
        <Textarea
          value={formData.notes || ""}
          onChange={(e) => updateField("notes", e.target.value)}
          placeholder="Ej: Peso en crudo. Cocinar a la plancha..."
          className="bg-[#1A1A1A] border-[#2A2A2A] text-white min-h-[60px]"
          data-testid="food-notes-textarea"
        />
      </div>

      {/* Active toggle */}
      <div className="flex items-center justify-between p-3 bg-[#1A1A1A] rounded-lg border border-[#2A2A2A]">
        <div>
          <span className="text-white text-sm">Alimento activo</span>
          <p className="text-[#666] text-xs">Los alimentos inactivos no aparecen en el editor de dietas</p>
        </div>
        <button
          onClick={() => updateField("active", !formData.active)}
          className={cn(
            "w-12 h-6 rounded-full transition-colors relative",
            formData.active ? "bg-green-500" : "bg-[#2A2A2A]"
          )}
          data-testid="food-active-toggle"
        >
          <span className={cn(
            "absolute top-1 w-4 h-4 bg-white rounded-full transition-all",
            formData.active ? "left-7" : "left-1"
          )} />
        </button>
      </div>

      {/* Actions */}
      <div className="flex gap-3 pt-4 border-t border-[#2A2A2A]">
        <Button
          variant="outline"
          onClick={onCancel}
          className="flex-1 border-[#2A2A2A] text-white"
          data-testid="food-cancel-btn"
        >
          Cancelar
        </Button>
        <Button
          onClick={() => onSave(formData)}
          disabled={saving || !formData.name}
          className="flex-1 bg-[#D4AF37] hover:bg-[#B8962E] text-[#0B0B0B]"
          data-testid="food-save-btn"
        >
          {saving ? "Guardando..." : "Guardar"}
        </Button>
      </div>
    </div>
  );
};

// Main Admin Foods Page
const AdminFoods = () => {
  const navigate = useNavigate();
  const { getAuthHeaders } = useAuth();
  const [foods, setFoods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [showEditor, setShowEditor] = useState(false);
  const [editingFood, setEditingFood] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const fetchFoods = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/foods`, { headers: getAuthHeaders() });
      setFoods(response.data.foods || []);
    } catch (error) {
      toast.error("Error al cargar alimentos");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFoods();
  }, []);

  const handleCreate = () => {
    setEditingFood(null);
    setShowEditor(true);
  };

  const handleEdit = (food) => {
    setEditingFood(food);
    setShowEditor(true);
  };

  const handleSave = async (formData) => {
    setSaving(true);
    try {
      if (editingFood) {
        await axios.put(
          `${API_URL}/api/admin/foods/${editingFood.id}`,
          formData,
          { headers: getAuthHeaders() }
        );
        toast.success("Alimento actualizado");
      } else {
        await axios.post(
          `${API_URL}/api/admin/foods`,
          formData,
          { headers: getAuthHeaders() }
        );
        toast.success("Alimento creado");
      }
      setShowEditor(false);
      fetchFoods();
    } catch (error) {
      toast.error("Error al guardar alimento");
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (food) => {
    try {
      await axios.patch(
        `${API_URL}/api/admin/foods/${food.id}/toggle`,
        {},
        { headers: getAuthHeaders() }
      );
      toast.success(food.active ? "Alimento desactivado" : "Alimento activado");
      fetchFoods();
    } catch (error) {
      toast.error("Error al cambiar estado");
    }
  };

  const handleDelete = async (food) => {
    try {
      await axios.delete(
        `${API_URL}/api/admin/foods/${food.id}`,
        { headers: getAuthHeaders() }
      );
      toast.success("Alimento eliminado");
      setDeleteConfirm(null);
      fetchFoods();
    } catch (error) {
      toast.error("Error al eliminar alimento");
    }
  };

  const getCategoryInfo = (catId) => CATEGORIES.find(c => c.id === catId) || CATEGORIES[CATEGORIES.length - 1];

  const filteredFoods = foods.filter(f => {
    const matchesSearch = f.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         f.notes?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = categoryFilter === "all" || f.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  const activeCount = foods.filter(f => f.active !== false).length;
  const inactiveCount = foods.filter(f => f.active === false).length;

  // Group foods by category for stats
  const categoryStats = CATEGORIES.map(cat => ({
    ...cat,
    count: foods.filter(f => f.category === cat.id).length
  })).filter(c => c.count > 0);

  return (
    <div className="min-h-screen bg-[#0B0B0B] pb-24" data-testid="admin-foods-page">
      {/* Header */}
      <div className="sticky top-0 bg-[#0B0B0B]/95 backdrop-blur-sm border-b border-[#2A2A2A] p-4 z-10">
        <div className="flex items-center gap-3 mb-4">
          <button 
            onClick={() => navigate("/admin")} 
            className="p-2 -ml-2 text-white hover:bg-white/10 rounded-lg"
            data-testid="admin-foods-back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex-1">
            <h1 className="text-white font-bold text-lg">Biblioteca de Alimentos</h1>
            <p className="text-[#666] text-xs">
              {foods.length} alimentos ({activeCount} activos, {inactiveCount} ocultos)
            </p>
          </div>
          <Button
            onClick={handleCreate}
            className="bg-[#D4AF37] hover:bg-[#B8962E] text-[#0B0B0B]"
            data-testid="admin-foods-create-btn"
          >
            <Plus className="w-4 h-4 mr-2" /> Nuevo
          </Button>
        </div>

        {/* Search */}
        <div className="relative mb-3">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#666]" />
          <Input
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Buscar alimentos..."
            className="pl-10 bg-[#1A1A1A] border-[#2A2A2A] text-white"
            data-testid="admin-foods-search"
          />
        </div>

        {/* Category Filter */}
        <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
          <button
            onClick={() => setCategoryFilter("all")}
            className={cn(
              "px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-all flex-shrink-0",
              categoryFilter === "all"
                ? "bg-[#D4AF37] text-[#0B0B0B]"
                : "bg-[#1A1A1A] text-[#888] hover:bg-[#2A2A2A]"
            )}
            data-testid="filter-all"
          >
            Todos ({foods.length})
          </button>
          {categoryStats.map(cat => (
            <button
              key={cat.id}
              onClick={() => setCategoryFilter(cat.id)}
              className={cn(
                "px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-all flex-shrink-0",
                categoryFilter === cat.id
                  ? `${cat.bg} ${cat.color} ring-1 ring-current`
                  : "bg-[#1A1A1A] text-[#888] hover:bg-[#2A2A2A]"
              )}
              data-testid={`filter-${cat.id}`}
            >
              {cat.label} ({cat.count})
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="p-4">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : filteredFoods.length === 0 ? (
          <div className="text-center py-20">
            <Apple className="w-12 h-12 text-[#444] mx-auto mb-4" />
            <p className="text-[#666]">
              {searchTerm || categoryFilter !== "all" ? "No se encontraron alimentos" : "No hay alimentos"}
            </p>
            {!searchTerm && categoryFilter === "all" && (
              <Button onClick={handleCreate} className="mt-4 bg-[#D4AF37] text-[#0B0B0B]">
                Crear primer alimento
              </Button>
            )}
          </div>
        ) : (
          <div className="space-y-2">
            {filteredFoods.map(food => {
              const category = getCategoryInfo(food.category);
              return (
                <div 
                  key={food.id}
                  className={cn(
                    "bg-[#1A1A1A] rounded-xl p-4 border transition-all",
                    food.active !== false 
                      ? "border-[#2A2A2A]" 
                      : "border-[#2A2A2A] opacity-60"
                  )}
                  data-testid={`admin-food-${food.id}`}
                >
                  <div className="flex items-start gap-3">
                    {/* Category badge */}
                    <div className={cn(
                      "w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0",
                      category.bg
                    )}>
                      <category.icon className={cn("w-5 h-5", category.color)} />
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="text-white font-semibold truncate">{food.name}</h3>
                        {food.active === false && (
                          <span className="px-2 py-0.5 bg-[#2A2A2A] text-[#666] text-[10px] rounded flex-shrink-0">
                            OCULTO
                          </span>
                        )}
                      </div>
                      
                      {/* Macros row */}
                      <div className="flex items-center gap-3 text-xs mb-1">
                        <span className="text-[#D4AF37] font-medium">{food.calories_per_100} kcal</span>
                        <span className="text-red-400">P: {food.protein_per_100}g</span>
                        <span className="text-yellow-400">C: {food.carbs_per_100}g</span>
                        <span className="text-blue-400">G: {food.fat_per_100}g</span>
                      </div>
                      
                      {/* Category and default */}
                      <div className="flex items-center gap-2 text-xs text-[#666]">
                        <span className={cn("px-2 py-0.5 rounded", category.bg, category.color)}>
                          {category.label}
                        </span>
                        <span>Por defecto: {food.default_amount}{food.unit}</span>
                      </div>
                      
                      {/* Notes */}
                      {food.notes && (
                        <p className="text-[#555] text-xs mt-1 truncate">{food.notes}</p>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button
                        onClick={() => handleToggle(food)}
                        className={cn(
                          "p-2 rounded transition-colors",
                          food.active !== false 
                            ? "text-green-400 hover:bg-green-500/10" 
                            : "text-[#666] hover:bg-[#2A2A2A]"
                        )}
                        title={food.active !== false ? "Ocultar" : "Activar"}
                        data-testid={`toggle-food-${food.id}`}
                      >
                        {food.active !== false ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                      </button>
                      <button
                        onClick={() => handleEdit(food)}
                        className="p-2 text-[#D4AF37] hover:bg-[#D4AF37]/10 rounded"
                        title="Editar"
                        data-testid={`edit-food-${food.id}`}
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setDeleteConfirm(food)}
                        className="p-2 text-red-400 hover:bg-red-500/10 rounded"
                        title="Eliminar"
                        data-testid={`delete-food-${food.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Food Editor Dialog */}
      <Dialog open={showEditor} onOpenChange={setShowEditor}>
        <DialogContent className="bg-[#121212] border-[#2A2A2A] text-white max-w-2xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="text-[#D4AF37]">
              {editingFood ? "Editar alimento" : "Nuevo alimento"}
            </DialogTitle>
          </DialogHeader>
          <FoodForm
            food={editingFood}
            onSave={handleSave}
            onCancel={() => setShowEditor(false)}
            saving={saving}
          />
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <DialogContent className="bg-[#121212] border-[#2A2A2A] text-white max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-white">¿Eliminar alimento?</DialogTitle>
          </DialogHeader>
          <p className="text-[#888] text-sm">
            Vas a eliminar "{deleteConfirm?.name}". Si este alimento está siendo usado en dietas existentes, 
            considera desactivarlo en lugar de eliminarlo.
          </p>
          <div className="flex gap-3 mt-4">
            <Button
              variant="outline"
              onClick={() => setDeleteConfirm(null)}
              className="flex-1 border-[#2A2A2A] text-white"
            >
              Cancelar
            </Button>
            <Button
              onClick={() => handleDelete(deleteConfirm)}
              className="flex-1 bg-red-500 hover:bg-red-600 text-white"
              data-testid="confirm-delete-food-btn"
            >
              Eliminar
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminFoods;
